import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Loader2, Package, Search, FolderPlus } from "lucide-react";
import { api } from "../../api";

const BLANK = { name: "", short: "", price: 0, compareAt: null, image: "", categories: [], tags: [], badge: "", stock: 0, description: "", rating: 4.8, reviews: 0, videoId: "" };
const CAT_BLANK = { key: "", label: "", blurb: "", image: "", order: 10 };

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [catModal, setCatModal] = useState(false);
  const [catForm, setCatForm] = useState(CAT_BLANK);
  const [catBusy, setCatBusy] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  const loadCats = () => api.get("/admin/sections").then((r) => setCats(r.data));
  useEffect(() => { load(); loadCats(); }, []);

  const saveCat = async () => {
    setCatBusy(true);
    const payload = { ...catForm, key: catForm.key.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""), order: Number(catForm.order) };
    try {
      await api.post("/admin/sections", payload);
      await loadCats();
      setCatModal(false);
      setCatForm(CAT_BLANK);
    } catch (e) { alert(e?.response?.data?.detail || "Could not add category"); }
    finally { setCatBusy(false); }
  };

  const openNew = () => { setEditing("new"); setForm(BLANK); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...BLANK, ...p, badge: p.badge || "", videoId: p.videoId || "" }); };
  const close = () => setEditing(null);

  const toggleCat = (c) => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));

  const save = async () => {
    setBusy(true);
    const payload = { ...form, price: Number(form.price), stock: Number(form.stock), reviews: Number(form.reviews), rating: Number(form.rating), compareAt: form.compareAt ? Number(form.compareAt) : null, badge: form.badge || null, videoId: form.videoId || null, tags: form.tags };
    try {
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing}`, payload);
      await load(); close();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); load();
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase text-[#1B2a21]">Products</h1>
          <p className="text-[#6b7d72]">{products.length} products · add, edit, remove & manage stock</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 rounded-full bg-[#2D6A4F] px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#1B4332]"><Plus size={18} /> Add Product</button>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-[#dceadf] bg-white px-4 py-2.5">
          <Search size={17} className="text-[#8aa093]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className="w-full bg-transparent text-sm outline-none" />
        </div>
        <button onClick={() => { setCatForm(CAT_BLANK); setCatModal(true); }} className="flex items-center gap-2 rounded-full border-2 border-[#2D6A4F] bg-white px-6 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-[#2D6A4F] transition hover:bg-[#f0f7f2]"><FolderPlus size={18} /> Add Category</button>
      </div>

      {loading ? <Loader2 className="animate-spin text-[#2D6A4F]" /> : (
        <div className="overflow-x-auto rounded-2xl border border-[#e6f0ea] bg-white">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-[#f0f7f2] font-display text-xs uppercase tracking-wider text-[#2D6A4F]">
              <tr><th className="p-4">Product</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4">Categories</th><th className="p-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-[#f0f5f2]">
                  <td className="p-4"><div className="flex items-center gap-3"><img src={p.image} alt="" className="h-11 w-11 rounded-lg bg-[#f3f8f4] object-contain p-1" /><span className="font-semibold text-[#1B2a21]">{p.short || p.name}</span></div></td>
                  <td className="p-4 font-semibold text-[#1B4332]">£{Number(p.price).toFixed(2)}</td>
                  <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${p.stock > 50 ? "bg-[#e3f3ea] text-[#2D6A4F]" : p.stock > 0 ? "bg-[#fff4e0] text-[#b4790a]" : "bg-[#fbe9e9] text-[#c1121f]"}`}>{p.stock} in stock</span></td>
                  <td className="p-4 text-[#6b7d72]">{(p.categories || []).join(", ")}</td>
                  <td className="p-4"><div className="flex justify-end gap-2"><button onClick={() => openEdit(p)} className="rounded-lg p-2 text-[#2D6A4F] hover:bg-[#f0f7f2]"><Pencil size={16} /></button><button onClick={() => del(p.id)} className="rounded-lg p-2 text-[#c1121f] hover:bg-[#fbe9e9]"><Trash2 size={16} /></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold uppercase text-[#1B4332]">{editing === "new" ? "Add Product" : "Edit Product"}</h2>
              <button onClick={close}><X size={22} className="text-[#1B4332]" /></button>
            </div>
            <div className="space-y-3">
              {form.image && <img src={form.image} alt="" className="mx-auto h-28 w-28 rounded-xl bg-[#f3f8f4] object-contain p-2" />}
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="Short name" value={form.short} onChange={(v) => setForm({ ...form, short: v })} />
              <Field label="Image URL (photo)" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
              <div className="grid grid-cols-3 gap-3">
                <Field label="Price £" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
                <Field label="Was £ (opt)" type="number" value={form.compareAt || ""} onChange={(v) => setForm({ ...form, compareAt: v })} />
                <Field label="Stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Badge (opt)" value={form.badge} onChange={(v) => setForm({ ...form, badge: v })} />
                <Field label="TikTok video ID (opt)" value={form.videoId} onChange={(v) => setForm({ ...form, videoId: v })} />
              </div>
              <div>
                <label className="mb-1 block font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F]">Sections</label>
                <div className="flex flex-wrap gap-2">
                  {cats.map((c) => (
                    <button key={c.key} onClick={() => toggleCat(c.key)} className={`rounded-full px-3 py-1.5 text-xs font-semibold uppercase transition ${form.categories.includes(c.key) ? "bg-[#2D6A4F] text-white" : "bg-[#f0f7f2] text-[#2D6A4F]"}`}>{c.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1 block font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F]">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full rounded-xl border border-[#dceadf] px-4 py-2.5 text-sm outline-none focus:border-[#52B788]" />
              </div>
              <button onClick={save} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2D6A4F] py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#1B4332] disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />} {editing === "new" ? "Create Product" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {catModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCatModal(false)} />
          <div className="relative w-full max-w-md rounded-3xl bg-white p-6">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold uppercase text-[#1B4332]"><FolderPlus size={20} /> Add Category</h2>
              <button onClick={() => setCatModal(false)}><X size={22} className="text-[#1B4332]" /></button>
            </div>
            <p className="mb-4 text-sm text-[#6b7d72]">New categories appear on the website nav, the home page tiles and the shop filters.</p>
            <div className="space-y-3">
              {catForm.image && <img src={catForm.image} alt="" className="h-32 w-full rounded-xl object-cover" />}
              <Field label="Name shown on site (e.g. Kids, Sleep, Vegan)" value={catForm.label} onChange={(v) => setCatForm({ ...catForm, label: v, key: catForm.key || v })} />
              <Field label="Short tagline" value={catForm.blurb} onChange={(v) => setCatForm({ ...catForm, blurb: v })} />
              <Field label="Image URL (photo tile)" value={catForm.image} onChange={(v) => setCatForm({ ...catForm, image: v })} />
              <Field label="Order (position in nav)" type="number" value={catForm.order} onChange={(v) => setCatForm({ ...catForm, order: v })} />
              <button onClick={saveCat} disabled={catBusy || !catForm.label} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2D6A4F] py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#1B4332] disabled:opacity-60">
                {catBusy && <Loader2 size={16} className="animate-spin" />} Create Category
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="mb-1 block font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F]">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-[#dceadf] px-4 py-2.5 text-sm outline-none focus:border-[#52B788]" />
  </div>
);

export default AdminProducts;
